import React, { useState } from "react";
import DashboardModal from "@/components/dashboard-modal";
import { useAutoNavigation } from "@/hooks/nav/use-auto-navigation";
import { NewWorkbookForm, NewWorkbookFormState } from "./NewWorkbookForm";
import NewWorkbookFooter from "./NewWorkbookFooter";
import { useToast } from "@/hooks/errors/use-toast";
import { useAIUsageData } from "@/hooks/subscription/useAIUsageData";
import { useGlobalModals } from "@/contexts/GlobalModalsContext";
import ProjectGenerationModal from "./ProjectGenerationModal";
import { ProjectGenerationRequest } from "@/types/project-generation-types";

interface NewWorkbookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated?: (workbookId: string, goalId: string) => void;
}

const INITIAL_STATE: NewWorkbookFormState = {
  userGoal: ""
};

const NewWorkbookModal: React.FC<NewWorkbookModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated
}) => {
  const [form, setForm] = useState<NewWorkbookFormState>(INITIAL_STATE);
  const [showValidation, setShowValidation] = useState(false);
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [generationRequest, setGenerationRequest] = useState<ProjectGenerationRequest | null>(null);

  const { goToWorkbookEditor, goToTaskManager } = useAutoNavigation();
  const { toast } = useToast();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();

  const handleClose = () => {
    setForm(INITIAL_STATE);
    setShowValidation(false);
    onClose();
  };

  const handleSubmit = async () => {
    setShowValidation(true);
    if (!form.userGoal || form.userGoal.length < 10) return;

    // ✅ UPFRONT CREDIT CHECK - Prevent operation if no credits available
    if (usageData.remainingBalance === 0) {
      console.log('[NewWorkbookModal] Insufficient credits - blocking project creation and showing modal');
      openOutOfCreditsModal();
      return; // Clean early exit - no cascading errors
    }

    console.log(`[NewWorkbookModal] Credits available (${usageData.remainingBalance}) - proceeding with project creation`);
    
    // Set up the generation request and open the progress modal
    setGenerationRequest({ userGoal: form.userGoal });
    setIsGenerationModalOpen(true);
  };

  const handleGenerationClose = () => {
    setIsGenerationModalOpen(false);
    setGenerationRequest(null);
  };

  const handleGenerationSuccess = (projectId: string, goalId: string) => {
    toast({
      title: "🎉 Project Created with AI!",
      description: "Your project has been created with AI-generated tasks and research recommendations.",
      variant: "success"
    });
    
    // Call the onProjectCreated callback if provided
    if (onProjectCreated) {
      onProjectCreated(projectId, goalId);
    } else {
      // Default behavior if no callback provided
      handleClose();
      goToTaskManager(goalId, projectId);
    }
  };

  // Validate: require userGoal (len>=10)
  const isValid = form.userGoal.length >= 10;

  return (
    <>
      <DashboardModal
        isOpen={isOpen && !isGenerationModalOpen}
        onClose={handleClose}
        title="Create Project"
      >
        <div className="space-y-6">
          <NewWorkbookForm
            value={form}
            onChange={setForm}
            showValidation={showValidation}
          />
          
          <NewWorkbookFooter
            onCancel={handleClose}
            onSubmit={handleSubmit}
            isValid={isValid}
            isSubmitting={false}
            submitText="Create Project"
          />
        </div>
      </DashboardModal>

      {/* Project Generation Progress Modal */}
      <ProjectGenerationModal
        isOpen={isGenerationModalOpen}
        onClose={handleGenerationClose}
        requestData={generationRequest}
        onSuccess={handleGenerationSuccess}
      />
    </>
  );
};

export default NewWorkbookModal;
